import AsyncStorage from '@react-native-async-storage/async-storage';
import VersionService from '../VersionService/VersionService';
import { Platform } from 'react-native';

interface VersionData {
  version: string;
  build: number;
  expirationTime: number;
}

interface PlatformVersion {
  build: number;
  version: string;
  has_problem: boolean;
}

interface ApiResponse {
  android: PlatformVersion;
  ios: PlatformVersion;
}

interface ValidationResult {
  isOutdated: boolean;
  hasProblem: boolean;
}

const TOKEN_EXPIRATION_TIME = 60 * 60 * 1000; // 1 hora

const saveVersionDataToStorage = async (data: VersionData) => {
  try {
    const expirationTime = Date.now() + TOKEN_EXPIRATION_TIME;
    await AsyncStorage.setItem('versionData', JSON.stringify({ ...data, expirationTime }));
  } catch (error) {
    console.log('Erro ao salvar dados:', error);
  }
};

const removeVersionDataFromStorage = async () => {
  try {
    await AsyncStorage.removeItem('versionData');
  } catch (error) {
    console.log('Erro ao remover dados:', error);
  }
};

const getVersionDataFromStorage = async (): Promise<VersionData | null> => {
  try {
    const storedData = await AsyncStorage.getItem('versionData');
    return storedData ? JSON.parse(storedData) : null;
  } catch (error) {
    console.log('Erro ao obter dados:', error);
    return null;
  }
};

const compareVersions = (v1: string, v2: string): number => {
  const v1Parts = v1.split('.').map(Number);
  const v2Parts = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const part1 = v1Parts[i] || 0;
    const part2 = v2Parts[i] || 0;

    if (part1 < part2) {
      return -1;
    } else if (part1 > part2) {
      return 1;
    }
  }

  return 0;
};

const validateVersion = async (appVersion: string, buildNumber: number): Promise<ValidationResult> => {
  const defaultResult: ValidationResult = { isOutdated: false, hasProblem: false };

  try {
    if (Platform.OS === 'web') {
      return false;
    }
    const response: ApiResponse = await VersionService.VersionGet();
    const deviceOS: 'android' | 'ios' = Platform.OS as 'android' | 'ios';
    const versionInfo = response[deviceOS];

    if (versionInfo.has_problem) {
      return { isOutdated: false, hasProblem: true };
    }

    const storedData: VersionData | null = await getVersionDataFromStorage();

    if (storedData && storedData.expirationTime > Date.now()) {
      const isOutdated =
        compareVersions(appVersion, storedData.version) < 0 ||
        (compareVersions(appVersion, storedData.version) === 0 && buildNumber < storedData.build);

      if (isOutdated) {
        await removeVersionDataFromStorage();
      }

      return { isOutdated, hasProblem: false };
    }

    const isOutdated =
      compareVersions(appVersion, versionInfo.version) < 0 ||
      (compareVersions(appVersion, versionInfo.version) === 0 && buildNumber < versionInfo.build);

    if (isOutdated) {
      await removeVersionDataFromStorage();
    } else {
      await saveVersionDataToStorage({
        version: versionInfo.version,
        build: versionInfo.build,
        expirationTime: Date.now() + TOKEN_EXPIRATION_TIME,
      });
    }

    return { isOutdated, hasProblem: false };
  } catch (error) {
    console.log('Erro ao buscar versão:', error);
    return defaultResult;
  }
};

export default {
  validateVersion,
};
